import type { RefObject } from "react"
import { useEffect, useRef } from "react"

const INTERACTIVE_SELECTOR = [
	"a[href]",
	"button",
	"input",
	"select",
	"textarea",
	"label",
	"summary",
	"[contenteditable]:not([contenteditable='false'])",
	"[tabindex]",
	"[role='button']",
	"[role='checkbox']",
	"[role='combobox']",
	"[role='link']",
	"[role='menuitem']",
	"[role='option']",
	"[role='separator']",
	"[role='switch']",
	"[role='tab']",
	"[role='textbox']",
	// Bubble menus and popovers mounted inside the box own their own clicks.
	"[data-tippy-root]",
	"[data-radix-popper-content-wrapper]",
].join(",")

/**
 * True when a press inside the composer box lands on padding, gaps or static
 * text rather than the editor or a control. Left alone, the browser focuses
 * nothing typeable there, so the next keystrokes fire thread hotkeys.
 */
export const isComposerDeadZone = (
	target: Element,
	container: HTMLElement,
): boolean => {
	if (!container.contains(target)) return false
	const interactive = target.closest(INTERACTIVE_SELECTOR)
	return !interactive || !container.contains(interactive)
}

// A press on an element's own scrollbar reports the element as its target.
const isOnScrollbar = (event: MouseEvent, target: Element): boolean =>
	event.offsetX > target.clientWidth || event.offsetY > target.clientHeight

/** Sends every press on a dead zone of the composer box to its editor. */
export function useComposerDeadZoneFocus({
	containerRef,
	enabled,
	onDeadZonePress,
}: {
	containerRef: RefObject<HTMLElement | null>
	enabled: boolean
	onDeadZonePress: () => void
}): void {
	const onDeadZonePressRef = useRef(onDeadZonePress)
	onDeadZonePressRef.current = onDeadZonePress

	useEffect(() => {
		const container = containerRef.current
		if (!enabled || !container) return
		const onMouseDown = (event: MouseEvent) => {
			if (event.button !== 0) return
			const target = event.target
			if (!(target instanceof Element)) return
			if (!isComposerDeadZone(target, container)) return
			if (isOnScrollbar(event, target)) return
			// Keeps the browser from moving focus to the body after we focus the editor.
			event.preventDefault()
			onDeadZonePressRef.current()
		}
		container.addEventListener("mousedown", onMouseDown)
		return () => container.removeEventListener("mousedown", onMouseDown)
	}, [enabled, containerRef])
}
